/* eslint:disable:max-classes-per-file no-empty */
import { datadogRum, type RumGlobal } from "@datadog/browser-rum";

import Sentry from "@similarweb/sentry-logger";
import type { CustomAttribute, Primitive, TrackModel, ITrack } from "@similarweb/sw-track";
import {
    ConsoleTracker,
    DatadogTracker,
    GoogleTracker,
    IntercomTracker,
    MatomoTracker,
    MunchkinTracker,
    trackerWithConsole,
} from "@similarweb/sw-track";

import { isInitialized } from "app/datadogRum";
import isRouteToSales from "app/helpers/is/isRouteToSales";
import SIAllTrackersService from "app/pages/sales-intelligence/services/tracking/SIAllTrackersService";
import type { AllTrackersBaseService } from "app/services/track/AllTrackersService";
import { AllTrackersService } from "app/services/track/AllTrackersService";
import { MixpanelTracker } from "app/services/track/trackers/MixpanelTracker";
import { locationService } from "single-spa/react-app/router/router";

declare const SW_ENV: {
    debug: boolean;
};

export type CustomAttributes = Record<CustomAttribute, Primitive>;

// tracker instances
const matomoTrackerInt = new (class extends MatomoTracker {
    protected paq(): any[] {
        return window._matomoPaq;
    }

    protected shouldBuffer(): boolean {
        return window.similarweb.config.trackBuffer;
    }

    protected searchParams() {
        return locationService.search();
    }

    protected isEnabled(): boolean {
        return !SW_ENV.debug;
    }
})();

const munchkinTrackerInt = new (class extends MunchkinTracker {
    protected munchkin(): (...params: unknown[]) => void {
        return window.MunchkinAsync;
    }

    protected location(): string {
        return locationService.url();
    }

    protected searchParams() {
        return locationService.search();
    }

    protected marketoEndpoint(): string {
        return "/api/marketo";
    }

    protected isEnabled(): boolean {
        return !SW_ENV.debug;
    }
})();

const googleTrackerInt = new (class extends GoogleTracker {
    protected gtag(): (...params: unknown[]) => void {
        return window.gtag;
    }

    protected isEnabled(): boolean {
        return !SW_ENV.debug;
    }

    protected trackPageViewInternal(): void {
        try {
            const gtag = this.gtag();
            if (gtag) {
                gtag("event", "page_view", {
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    page_location: document.location.pathname + document.location.hash,
                });
            }
        } catch (e) {
            Sentry.errorTracker({
                message: "Error in google analytics 4",
                e,
                level: "error",
            });
        }
    }

    protected trackEventInternal({ category, action, name, trackId, location, value }: TrackModel): void {
        try {
            const gtag = this.gtag();
            if (gtag) {
                const eventNameForGA4 = trackId ?? name;
                gtag("event", eventNameForGA4, {
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    event_category: category,
                    action,
                    value,
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    page_location: location,
                });
            }
        } catch (e) {
            Sentry.errorTracker({
                message: "Error in google analytics 4",
                e,
                level: "error",
            });
        }
    }
})();

const intercomTrackerInt = new (class extends IntercomTracker {
    protected intercom(): (...params: unknown[]) => void {
        return window.Intercom;
    }

    protected isEnabled(): boolean {
        return !SW_ENV.debug;
    }
})();

const mixpanelTrackerInt = new (class extends MixpanelTracker {
    protected mixpanel() {
        return window.mixpanel;
    }

    protected isEnabled(): boolean {
        return !SW_ENV.debug;
    }

    protected onTrackerReady(cb: () => void): void {
        const mixpanel = this.mixpanel();

        if (this.isTrackerReady(mixpanel) && "unregister" in mixpanel) {
            cb();
        }

        $(document).on("MixpanelReady", cb);
    }

    resetCustomProperties(propertiesToReset: string[]): void {
        this.onTrackerReady(() => {
            const mixpanel = this.mixpanel();

            propertiesToReset.forEach((prop) => {
                mixpanel.unregister(prop);
            });
        });
    }
})();

const datadogRumTrackerInt = new (class extends DatadogTracker {
    protected isEnabled(): boolean {
        return !SW_ENV.debug && isInitialized();
    }

    protected datadogRum(): RumGlobal {
        return datadogRum;
    }
})();

// public trackers
// @ts-ignore FIXME: strict migration - Argument of type '(Anonymous class)' is not assignable to parameter of type 'ITrack' — upstream sw-track type mismatch
export const matomoTracker = trackerWithConsole(matomoTrackerInt, "matomoTracker");
// @ts-ignore FIXME: strict migration - Argument of type '(Anonymous class)' is not assignable to parameter of type 'ITrack' — upstream sw-track type mismatch
export const munchkinTracker = trackerWithConsole(munchkinTrackerInt, "munchkinTracker");
// @ts-ignore FIXME: strict migration - Argument of type '(Anonymous class)' is not assignable to parameter of type 'ITrack' — upstream sw-track type mismatch
export const googleTracker = trackerWithConsole(googleTrackerInt, "googleTracker");
// @ts-ignore FIXME: strict migration - Argument of type '(Anonymous class)' is not assignable to parameter of type 'ITrack' — upstream sw-track type mismatch
export const intercomTracker = trackerWithConsole(intercomTrackerInt, "intercomTracker");
// @ts-ignore FIXME: strict migration - Argument of type '(Anonymous class)' is not assignable to parameter of type 'ITrack' — upstream sw-track type mismatch
export const mixpanelTracker = trackerWithConsole(mixpanelTrackerInt, "mixpanelTracker");
// @ts-ignore FIXME: strict migration - Argument of type '(Anonymous class)' is not assignable to parameter of type 'ITrack' — upstream sw-track type mismatch
const datadogTracker = trackerWithConsole(datadogRumTrackerInt, "datadogTracker");

const trackers = [
    matomoTrackerInt,
    munchkinTrackerInt,
    googleTrackerInt,
    mixpanelTracker,
    /* _intercomTracker */
    new ConsoleTracker("allTrackers"),
] as unknown as ITrack[];

const pageViewTrackers = {
    matomoTracker,
    mixpanelTracker,
    munchkinTracker,
    datadogTracker,
};

const isSalesIntelligence = isRouteToSales();

if (!isSalesIntelligence) {
    mixpanelTrackerInt.resetCustomProperties(SIAllTrackersService.trackingParams);
}

export const allTrackers: AllTrackersBaseService = isSalesIntelligence
    ? new SIAllTrackersService(trackers, pageViewTrackers)
    : new AllTrackersService(trackers, pageViewTrackers);

export function healthChecks() {
    munchkinTracker.healthCheck();
    googleTracker.healthCheck();
    mixpanelTracker.healthCheck();
    matomoTracker.healthCheck();
    datadogTracker.healthCheck();
}
